/** Copyright 2025 Midas Healthcare Solutions - All Rights Reserved **/
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { RxJSBaseClass } from '@midas/shared/util';
import {
  auditTime,
  BehaviorSubject,
  filter,
  finalize,
  fromEvent,
  Observable,
  startWith,
  Subscription,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';

export interface PagedResult<T> {
  items: T[];
  count: number;
}

@Directive()
export abstract class IQuerySelect<T extends { id: number }>
  extends RxJSBaseClass
  implements OnInit, ControlValueAccessor, AfterViewInit, OnDestroy
{
  readonly searchCtrl = new FormControl('');

  filteredItems!: Observable<T[]>;

  items: T[] = [];
  total = Number.MAX_VALUE;

  private options: T[] = [];
  private options$ = new BehaviorSubject<T[]>([]);
  private pageSize = 10;
  loading = false;
  private currentQuery = '';

  separatorKeysCodes: number[] = [ENTER, COMMA];
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('auto') auto!: MatAutocomplete;

  private scrollSub!: Subscription;
  private panelScrollSub?: Subscription;
  changeFn!: (val: number[] | null) => unknown;

  abstract getItemsBySearchString(
    searchString?: string | null,
    startingIndex?: number,
    numberOfRecords?: number,
  ): Observable<PagedResult<T>>;
  abstract getItemsByIds(ids: number[]): Observable<T[]>;

  constructor(private change: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.filteredItems = this.options$.asObservable();

    this.searchCtrl.valueChanges
      .pipe(
        filter((str) => typeof str === 'string'),
        startWith(null),
        switchMap((str) => {
          this.currentQuery = str || '';
          this.options = [];
          this.options$.next(this.options);
          return this.getItemsBySearchString(this.currentQuery, 0, this.pageSize);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((pagedResults) => {
        this.options = pagedResults.items ?? [];
        this.options$.next(this.options);
        this.total = pagedResults.count;
        this.change.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    const scrollContainer = document.querySelector('.router-cont');
    if (scrollContainer) {
      this.scrollSub = fromEvent(scrollContainer, 'scroll').subscribe(() => {
        if (this.autocompleteTrigger?.panelOpen) {
          this.autocompleteTrigger.closePanel();
          this.change.detectChanges();
        }
      });
    }

    this.auto.opened.pipe(takeUntil(this.destroy$)).subscribe(() => this.onPanelOpened());
    this.auto.closed.pipe(takeUntil(this.destroy$)).subscribe(() => this.onPanelClosed());
  }

  onPanelOpened() {
    const panel = document.getElementById(this.auto.id) as HTMLElement | null;
    if (!panel) return;

    this.panelScrollSub = fromEvent(panel, 'scroll')
      .pipe(auditTime(0), takeUntil(this.auto.closed))
      .subscribe(() => {
        if (panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 48) this.loadMore();
      });
  }

  onPanelClosed() {
    this.panelScrollSub?.unsubscribe();
    this.panelScrollSub = undefined;
  }

  private loadMore() {
    if (this.loading || this.options.length >= this.total) return;

    this.loading = true;
    this.getItemsBySearchString(this.currentQuery, this.options.length, this.pageSize)
      .pipe(
        finalize(() => (this.loading = false)),
        take(1),
      )
      .subscribe((nextPage) => {
        if (!nextPage || nextPage.items.length === 0) return;
        this.options = this.options.concat(nextPage.items);
        this.options$.next(this.options);
        this.change.markForCheck();
      });
  }

  override ngOnDestroy(): void {
    this.scrollSub?.unsubscribe?.();
    this.panelScrollSub?.unsubscribe();
    super.ngOnDestroy?.();
  }

  remove(item: T): void {
    const index = this.items.findIndex((c) => c.id === item.id);

    if (index >= 0) {
      this.items.splice(index, 1);
    }

    this.changeFn(this.items.map((l) => l.id));
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const val = event.option.value as T;
    if (!this.items.some((c) => c.id === val.id)) {
      this.items.push(val);
    }
    this.searchInput.nativeElement.value = '';
    this.searchCtrl.setValue('');
    this.changeFn(this.items.map((l) => l.id));
  }

  writeValue(ids: number[]): void {
    if (!ids || ids.length === 0) {
      this.items = [];
      return;
    }

    this.getItemsByIds(ids)
      .pipe(take(1))
      .subscribe((items) => {
        this.items = items;
        this.change.markForCheck();
      });
  }

  registerOnChange(fn: (val: number[] | null) => unknown): void {
    this.changeFn = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnTouched(fn: any): void {
    this.searchCtrl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(fn);
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchCtrl.disable();
    } else {
      this.searchCtrl.enable();
    }
  }
}
